import React, { useState } from 'react';
import { CheckCircle, AlertCircle, Database, Users, ArrowLeft, FileText } from 'lucide-react';

const MigrationsPage = () => {
  const [migrationStatus, setMigrationStatus] = useState('idle');
  const [migrationResult, setMigrationResult] = useState(null);
  const [adminCreationStatus, setAdminCreationStatus] = useState('idle');
  const [adminResult, setAdminResult] = useState(null);
  const [documentsFixStatus, setDocumentsFixStatus] = useState('idle');
  const [documentsFixResult, setDocumentsFixResult] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || 'https://newgate-backend.vercel.app/api';

  const runMigration = async () => {
    setMigrationStatus('running');
    setMigrationResult(null);

    try {
      const response = await fetch(`${API_URL}/admin/migrate-database`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('adminToken') || 'temp-migration-token'}` 
        }
      });
      
      const result = await response.json();
      
      if (response.ok) {
        setMigrationStatus('success');
        setMigrationResult(result);
      } else {
        setMigrationStatus('error');
        setMigrationResult({ error: result.error || 'Migration failed' });
      }
    } catch (error) {
      setMigrationStatus('error');
      setMigrationResult({ error: error.message });
    }
  };

  const createAdminUser = async () => {
    const name = prompt('Enter admin name:');
    if (!name) return;
    
    const email = prompt('Enter admin email:');
    if (!email) return;
    
    const password = prompt('Enter admin password:');
    if (!password) return;

    setAdminCreationStatus('running');
    setAdminResult(null);

    try {
      const response = await fetch(`${API_URL}/admin/create-initial-admin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, password })
      });
      
      const result = await response.json();
      
      if (response.ok) {
        setAdminCreationStatus('success');
        setAdminResult({ ...result, credentials: { email, password } });
      } else {
        setAdminCreationStatus('error');
        setAdminResult({ error: result.error || 'Failed to create admin user' });
      }
    } catch (error) {
      setAdminCreationStatus('error');
      setAdminResult({ error: error.message });
    }
  };

  const fixDocumentsTable = async () => {
    setDocumentsFixStatus('running');
    setDocumentsFixResult(null);

    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        setDocumentsFixStatus('error');
        setDocumentsFixResult({ error: 'Please log in as admin first' });
        return;
      }

      const response = await fetch(`${API_URL}/admin/fix-documents-table`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}` 
        }
      });
      
      const result = await response.json();
      
      if (response.ok) {
        setDocumentsFixStatus('success');
        setDocumentsFixResult(result);
      } else {
        setDocumentsFixStatus('error');
        setDocumentsFixResult({ error: result.error || 'Failed to fix documents table' });
      }
    } catch (error) {
      setDocumentsFixStatus('error');
      setDocumentsFixResult({ error: error.message });
    }
  };

  const goToAdminPanel = () => {
    window.location.href = '/admin';
  };

  const goHome = () => {
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Database className="w-16 h-16 text-blue-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Database Migrations</h1>
          <p className="text-gray-600">Set up your Newgate Nurseries database</p>
        </div>

        {/* Navigation */}
        <div className="flex gap-4 mb-8 justify-center">
          <button
            onClick={goHome}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </button>
          <button
            onClick={goToAdminPanel}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            Go to Admin Panel
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Migration Card */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <Database className="w-8 h-8 text-blue-600" />
              <h2 className="text-xl font-semibold">Database Setup</h2>
            </div>
            
            <p className="text-gray-600 mb-6">
              Create the required database tables for payments, documents, admin users, and account management.
            </p>

            <button
              onClick={runMigration}
              disabled={migrationStatus === 'running'}
              className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                migrationStatus === 'running'
                  ? 'bg-gray-400 cursor-not-allowed'
                  : migrationStatus === 'success'
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-blue-600 hover:bg-blue-700'
              } text-white`}
            >
              {migrationStatus === 'running' && 'Running Migration...'}
              {migrationStatus === 'success' && 'Migration Complete ✓'}
              {migrationStatus === 'error' && 'Retry Migration'}
              {migrationStatus === 'idle' && 'Run Database Migration'}
            </button>

            {migrationResult && (
              <div className={`mt-4 p-4 rounded-lg ${
                migrationStatus === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
              }`}>
                {migrationStatus === 'success' ? (
                  <div>
                    <div className="flex items-center gap-2 text-green-800 mb-2">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-medium">Migration Successful!</span>
                    </div>
                    <div className="text-sm text-green-700">
                      <p className="mb-2">Tables created:</p>
                      <ul className="list-disc list-inside space-y-1">
                        {migrationResult.details?.tablesCreated?.map((table, index) => (
                          <li key={index}>{table}</li>
                        ))}
                      </ul>
                      {migrationResult.details?.quotesUpdated > 0 && (
                        <p className="mt-2">Updated {migrationResult.details.quotesUpdated} existing quotes</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center gap-2 text-red-800 mb-2">
                      <AlertCircle className="w-5 h-5" />
                      <span className="font-medium">Migration Failed</span>
                    </div>
                    <p className="text-sm text-red-700">{migrationResult.error}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Admin User Creation Card */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <Users className="w-8 h-8 text-purple-600" />
              <h2 className="text-xl font-semibold">Admin User Setup</h2>
            </div>
            
            <p className="text-gray-600 mb-6">
              Create your first admin user to access the admin panel. Run this after the database migration.
            </p>

            <button
              onClick={createAdminUser}
              disabled={adminCreationStatus === 'running' || migrationStatus !== 'success'}
              className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                migrationStatus !== 'success'
                  ? 'bg-gray-300 cursor-not-allowed text-gray-500'
                  : adminCreationStatus === 'running'
                  ? 'bg-gray-400 cursor-not-allowed text-white'
                  : adminCreationStatus === 'success'
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-purple-600 hover:bg-purple-700 text-white'
              }`}
            >
              {adminCreationStatus === 'running' && 'Creating Admin User...'}
              {adminCreationStatus === 'success' && 'Admin User Created ✓'}
              {adminCreationStatus === 'error' && 'Retry Create Admin'}
              {adminCreationStatus === 'idle' && migrationStatus === 'success' && 'Create Admin User'}
              {adminCreationStatus === 'idle' && migrationStatus !== 'success' && 'Run Migration First'}
            </button>

            {adminResult && (
              <div className={`mt-4 p-4 rounded-lg ${
                adminCreationStatus === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
              }`}>
                {adminCreationStatus === 'success' ? (
                  <div>
                    <div className="flex items-center gap-2 text-green-800 mb-2">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-medium">Admin User Created!</span>
                    </div>
                    <div className="text-sm text-green-700">
                      <p className="mb-2">Login credentials:</p>
                      <div className="bg-white p-3 rounded border">
                        <p><strong>Email:</strong> {adminResult.credentials?.email}</p>
                        <p><strong>Password:</strong> {adminResult.credentials?.password}</p>
                      </div>
                      <p className="mt-2 text-green-600">Save these credentials securely!</p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center gap-2 text-red-800 mb-2">
                      <AlertCircle className="w-5 h-5" />
                      <span className="font-medium">Creation Failed</span>
                    </div>
                    <p className="text-sm text-red-700">{adminResult.error}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Fix Documents Table Card - NEW */}
        <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-8 h-8 text-orange-600" />
            <h2 className="text-xl font-semibold">Fix Documents Table</h2>
          </div>
          
          <p className="text-gray-600 mb-6">
            If you're experiencing issues with PDF broadcasts (column "title" errors), run this to recreate the documents table with the correct structure.
          </p>

          <button
            onClick={fixDocumentsTable}
            disabled={documentsFixStatus === 'running'}
            className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
              documentsFixStatus === 'running'
                ? 'bg-gray-400 cursor-not-allowed'
                : documentsFixStatus === 'success'
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-orange-600 hover:bg-orange-700'
            } text-white`}
          >
            {documentsFixStatus === 'running' && 'Fixing Documents Table...'}
            {documentsFixStatus === 'success' && 'Documents Table Fixed ✓'}
            {documentsFixStatus === 'error' && 'Retry Fix'}
            {documentsFixStatus === 'idle' && 'Fix Documents Table'}
          </button>

          {documentsFixResult && (
            <div className={`mt-4 p-4 rounded-lg ${
              documentsFixStatus === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
            }`}>
              {documentsFixStatus === 'success' ? (
                <div>
                  <div className="flex items-center gap-2 text-green-800 mb-2">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Documents Table Fixed!</span>
                  </div>
                  <div className="text-sm text-green-700">
                    <p className="mb-2">Columns created:</p>
                    <ul className="list-disc list-inside space-y-1">
                      {documentsFixResult.columns?.map((col, index) => (
                        <li key={index}>{col}</li>
                      ))}
                    </ul>
                    {documentsFixResult.testInsert && (
                      <p className="mt-2">Test insert successful: {documentsFixResult.testInsert.title}</p>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-center gap-2 text-red-800 mb-2">
                    <AlertCircle className="w-5 h-5" />
                    <span className="font-medium">Fix Failed</span>
                  </div>
                  <p className="text-sm text-red-700">{documentsFixResult.error}</p>
                  {!localStorage.getItem('adminToken') && (
                    <p className="text-sm text-red-700 mt-2">
                      Tip: Log in to the admin panel first, then come back to this page.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold mb-4">Instructions</h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>First, run the <strong>Database Migration</strong> to create all required tables</li>
            <li>Then, create your <strong>Admin User</strong> to access the admin panel</li>
            <li>Save your admin credentials securely</li>
            <li>Go to the <strong>Admin Panel</strong> and log in with your new credentials</li>
            <li>If PDF broadcasts fail with "column title does not exist", run <strong>Fix Documents Table</strong></li>
            <li><strong>Important:</strong> Remove this migrations page from your production site after setup</li>
          </ol>
        </div>

        {/* Warning */}
        <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div className="text-yellow-800">
              <p className="font-medium">Security Notice</p>
              <p className="text-sm mt-1">
                This migrations page should be removed from your production site after setup is complete. 
                It's only intended for initial database setup.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MigrationsPage;